import React, { ReactNode } from 'react';
import './Chip.css';

export interface ChipProps {
  label: string;
  /** Native exclusive selection; retains browser arrow-key behavior. */
  radioName?: string;
  radioValue?: string;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
  /** Id of the element this chip controls (a sheet or a filtered region). */
  controls?: string;
  /** Set when the chip opens a dialog instead of toggling a filter. */
  haspopup?: 'dialog';
  expanded?: boolean;
  icon?: ReactNode;
  /** Appended to the accessible name after the label, for example the verb a trigger performs. */
  description?: string;
  /** Id of an element that describes the chip's current state. */
  describedBy?: string;
  className?: string;
  id?: string;
}

/**
 * Translucent filter chip. A toggle chip exposes aria-pressed; a chip that
 * opens a sheet exposes aria-haspopup instead and never claims pressed state.
 * Exclusive groups use the radioName mode backed by a native radio.
 */
const Chip: React.FC<ChipProps> = ({
  label,
  radioName,
  radioValue,
  selected = false,
  onSelect,
  disabled = false,
  controls,
  haspopup,
  expanded,
  icon,
  description,
  describedBy,
  className,
  id,
}) => {
  if (radioName) return <label className={`luma-chip-radio${className ? ` ${className}` : ''}`}>
    <input type="radio" id={id} name={radioName} value={radioValue ?? label} checked={selected} onChange={onSelect} disabled={disabled} aria-controls={controls} aria-describedby={describedBy} />
    {icon && <span className="luma-chip__icon" aria-hidden="true">{icon}</span>}<span>{label}</span>
  </label>;
  const isTrigger = haspopup === 'dialog';
  return (
    <button
      type="button"
      id={id}
      className={`luma-chip${selected ? ' luma-chip--selected' : ''}${className ? ` ${className}` : ''}`}
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={isTrigger ? undefined : selected}
      aria-haspopup={isTrigger ? 'dialog' : undefined}
      aria-expanded={isTrigger ? expanded : undefined}
      aria-controls={controls}
      aria-describedby={describedBy}
      aria-label={description ? `${label}, ${description}` : undefined}
    >
      {icon && <span className="luma-chip__icon" aria-hidden="true">{icon}</span>}
      <span className="luma-chip__label">{label}</span>
    </button>
  );
};

export default Chip;
